import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

const SearchAndFilters = ({ onSearch, onFilterChange, filters, searchQuery }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery || '');

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    onSearch(localQuery);
  };

  const handleFilterUpdate = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      difficulty: '',
      isPremium: null,
      status: ''
    };
    onFilterChange(clearedFilters);
    setLocalQuery('');
    onSearch('');
  };

  const hasActiveFilters = filters?.difficulty || filters?.isPremium !== null || filters?.status || searchQuery;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      {/* Search Bar */}
      <form onSubmit={handleSearchSubmit} className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={localQuery}
            onChange={(e) => setLocalQuery(e?.target?.value)}
            placeholder="Search modules by title, description, or content..."
            className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          {localQuery && (
            <button
              type="button"
              onClick={() => {
                setLocalQuery('');
                onSearch('');
              }}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </form>
      {/* Filter Toggle */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Filter className="h-4 w-4" />
          <span>Filters</span>
          {hasActiveFilters && (
            <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">
              {[filters?.difficulty, filters?.isPremium !== null, filters?.status, searchQuery]?.filter(Boolean)?.length}
            </span>
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
          >
            <X className="h-4 w-4 mr-1" />
            Clear all
          </button>
        )}
      </div>
      {/* Filter Options */}
      {showFilters && (
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulty Level
              </label>
              <select
                value={filters?.difficulty}
                onChange={(e) => handleFilterUpdate('difficulty', e?.target?.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            {/* Premium Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Type
              </label>
              <select
                value={filters?.isPremium === null ? '' : filters?.isPremium ? 'premium' : 'free'}
                onChange={(e) => {
                  const value = e?.target?.value;
                  handleFilterUpdate('isPremium', 
                    value === '' ? null : value === 'premium'
                  );
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All Content</option>
                <option value="free">Free Content</option>
                <option value="premium">Premium Content</option>
              </select>
            </div>

            {/* Progress Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Progress Status
              </label>
              <select
                value={filters?.status}
                onChange={(e) => handleFilterUpdate('status', e?.target?.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-2">Quick Filters:</div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleFilterUpdate('isPremium', false)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  filters?.isPremium === false
                    ? 'bg-green-100 text-green-800 border-green-300' :'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                }`}
              >
                Free Only
              </button>
              
              <button
                onClick={() => handleFilterUpdate('difficulty', 'beginner')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  filters?.difficulty === 'beginner' ?'bg-green-100 text-green-800 border-green-300' :'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                }`}
              >
                Beginner Friendly
              </button>
              
              <button
                onClick={() => handleFilterUpdate('status', 'not_started')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  filters?.status === 'not_started' ?'bg-blue-100 text-blue-800 border-blue-300' :'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                }`}
              >
                New to Me
              </button>
              
              <button
                onClick={() => handleFilterUpdate('status', 'in_progress')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  filters?.status === 'in_progress' ?'bg-yellow-100 text-yellow-800 border-yellow-300' :'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                }`}
              >
                Continue Learning
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm font-medium text-gray-700 mb-2">Active filters:</div>
          <div className="flex flex-wrap gap-2">
            {searchQuery && (
              <span className="bg-green-100 text-green-800 px-2 py-1 text-xs rounded-full flex items-center">
                Search: "{searchQuery}"
                <button
                  onClick={() => {
                    setLocalQuery('');
                    onSearch('');
                  }}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {filters?.difficulty && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs rounded-full flex items-center">
                {filters?.difficulty?.charAt(0)?.toUpperCase() + filters?.difficulty?.slice(1)}
                <button
                  onClick={() => handleFilterUpdate('difficulty', '')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {filters?.isPremium !== null && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1 text-xs rounded-full flex items-center">
                {filters?.isPremium ? 'Premium' : 'Free'} Content
                <button
                  onClick={() => handleFilterUpdate('isPremium', null)}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {filters?.status && (
              <span className="bg-orange-100 text-orange-800 px-2 py-1 text-xs rounded-full flex items-center">
                {filters?.status?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                <button
                  onClick={() => handleFilterUpdate('status', '')}
                  className="ml-1 text-orange-600 hover:text-orange-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchAndFilters;